import { Link } from "react-router-dom";
import { Check, Scissors, Trash2, Ruler, ShieldCheck, ArrowRight } from "lucide-react";
import PriceCalculator from "@/components/PriceCalculator";

const features = [
  { icon: Scissors, title: "Skarpt værktøj", text: "Professionelle klippere og skarpe klinger — for et rent snit hver gang." },
  { icon: Trash2, title: "Bortkørsel inkluderet", text: "Vi tager grene og affald med — du får en ren have tilbage." },
  { icon: Ruler, title: "Præcis linje", text: "Vi klipper efter snor og har øje for detaljen. Rette hjørner. Rette linjer." },
  { icon: ShieldCheck, title: "Fuldt forsikret", text: "Både medarbejdere og haven er forsikret gennem hele arbejdet." },
];

const process = [
  { n: "01", title: "Bestilling", text: "Ring, skriv eller brug beregneren. Vi svarer inden for 24 timer." },
  { n: "02", title: "Besigtigelse", text: "Vi kommer forbi (eller vurderer via billeder) og bekræfter prisen." },
  { n: "03", title: "Klipning", text: "Vi møder op til aftalt tid og klipper hækken pænt og præcist." },
  { n: "04", title: "Oprydning", text: "Grene og affald køres væk. Din have står klar og ren." },
];

export default function HedgeTrimming() {
  return (
    <div data-testid="page-hedge">
      <section className="mx-auto max-w-7xl px-6 lg:px-10 pt-16 pb-16 lg:pb-20">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.22em] text-brand-muted mb-4">Vores speciale</div>
            <h1 className="font-display text-5xl sm:text-6xl tracking-tight text-brand-text text-balance">
              Hækklipning der giver haven <span className="italic text-brand-primary">nyt liv</span>.
            </h1>
            <p className="mt-6 text-lg text-brand-muted max-w-2xl leading-relaxed">
              Vi klipper alle typer hække — bøg, thuja, liguster, laurbær, taks og mange flere. Fast pris fra 32 kr/m, inkl. bortkørsel og oprydning.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-brand-primary text-brand-bg rounded-3xl p-8">
              <div className="text-xs uppercase tracking-[0.22em] text-brand-bg/70">Startpris</div>
              <div className="font-display text-6xl mt-2">32 kr<span className="text-2xl">/m</span></div>
              <div className="text-sm text-brand-bg/80 mt-2">Inkl. bortkørsel, oprydning og moms</div>
              <ul className="mt-6 space-y-2 text-sm">
                {["Alle hæktyper og højder", "Bortkørsel af affald", "Ryddelig oprydning", "Fast pris — ingen overraskelser"].map((t) => (
                  <li key={t} className="flex items-center gap-2">
                    <Check className="h-4 w-4" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-20">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f, i) => (
            <div
              key={i}
              data-testid={`hedge-feature-${i}`}
              className="rounded-3xl border border-brand-border bg-brand-surface p-7"
            >
              <div className="h-11 w-11 rounded-2xl bg-brand-soft grid place-items-center">
                <f.icon className="h-5 w-5 text-brand-primary" strokeWidth={1.8} />
              </div>
              <h3 className="font-display text-lg mt-6 text-brand-text">{f.title}</h3>
              <p className="mt-2 text-sm text-brand-muted leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CALCULATOR */}
      <section id="beregner" className="mx-auto max-w-7xl px-6 lg:px-10 pb-20">
        <div className="text-xs uppercase tracking-[0.22em] text-brand-muted mb-3">Prisberegner</div>
        <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-brand-text max-w-2xl mb-12">
          Beregn din pris — <span className="italic">live og gratis</span>.
        </h2>
        <PriceCalculator />
      </section>

      {/* PROCESS */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-24">
        <div className="text-xs uppercase tracking-[0.22em] text-brand-muted mb-3">Sådan foregår det</div>
        <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-brand-text max-w-2xl mb-12">
          Fire enkle skridt — <span className="italic">fra opkald til færdig hæk</span>.
        </h2>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {process.map((s, i) => (
            <div
              key={i}
              data-testid={`hedge-step-${i}`}
              className="rounded-3xl bg-brand-surface border border-brand-border p-7"
            >
              <div className="font-display text-4xl text-brand-primary/30">{s.n}</div>
              <h3 className="font-display text-xl mt-4 text-brand-text">{s.title}</h3>
              <p className="mt-2 text-sm text-brand-muted leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 text-center">
          <Link
            to="/kontakt"
            data-testid="hedge-cta-quote"
            className="inline-flex items-center gap-2 rounded-full bg-brand-primary text-brand-bg px-8 py-4 font-medium hover:bg-brand-hover"
          >
            Få et tilbud <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
